// Single point of contact with Zoho PageSense. The tracker itself is loaded once per page load from
// public/index.html (only where VUE_APP_PAGESENSE_ENABLED is "true"); this service only queues
// commands on window.pagesense, which the tracker drains when it arrives. Any failure is swallowed:
// PageSense must never break the Manager.
const USER_CONTEXT_ACTIVITY = "UserContext";

// PageSense keeps every trackActivity call in localStorage under "z_user_info<projectKey>" and
// evaluates segmentation rules against ALL stored entries, so a previous user's context would
// still match. Its visitor identity lives in the zabUserId / zabVisitId cookies.
const CONTEXT_STORAGE_PREFIX = "z_user_info";
const VISITOR_COOKIES = ["zabUserId", "zabVisitId"];

function isEnabled() {
  return process.env.VUE_APP_PAGESENSE_ENABLED === "true";
}

function push(command) {
  if (!isEnabled() || typeof window.pagesense?.push !== "function") return;
  try {
    window.pagesense.push(command);
  } catch {
    // Do nothing
  }
}

function toNumericId(value) {
  if (value === null || value === undefined || value === "") return null;
  const numericId = Number(value);
  return Number.isFinite(numericId) ? numericId : null;
}

function clearStoredContext() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(CONTEXT_STORAGE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Do nothing
  }
}

function clearVisitorCookies() {
  const expired = "=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT";
  const domain = window.location.hostname.replace(/^www\./, "");
  VISITOR_COOKIES.forEach((name) => {
    // PageSense sets them on ".<host>", clear both scopes like its own expireNow() does
    document.cookie = `${name}${expired};domain=.${domain}`;
    document.cookie = `${name}${expired}`;
  });
}

// Identifies the authenticated user and sends its company context. Called at the same point as
// initZohoSalesIQ, so it runs on every login and on every reload with an active session.
export function identifyUser(userProfile) {
  if (!isEnabled()) return;
  const userID = toNumericId(userProfile?.id);
  const ownerID = toNumericId(userProfile?.ownerPerson?.id);
  if (userID === null || ownerID === null) return;
  // Keep only the current user's context, otherwise entries pile up on every reload
  clearStoredContext();
  push(["identifyUser", userID]);
  push([
    "trackActivity",
    USER_CONTEXT_ACTIVITY,
    {
      userID,
      owner: userProfile.ownerPerson.name || "",
      ownerID,
      userEmail: userProfile.email || userProfile.username || "",
    },
  ]);
}

// Drops the identity and context of the user that just logged out, so the next one in the same
// browser starts as a new anonymous visitor. PageSense has no logout command.
export function resetUser() {
  if (!isEnabled()) return;
  try {
    clearStoredContext();
    clearVisitorCookies();
  } catch {
    // Do nothing
  }
}

export default { identifyUser, resetUser };
